import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DataTable, type DataTableSortStatus, type DataTableColumn } from 'mantine-datatable';
import { Search, ChevronsLeft, ChevronsRight, X, Columns3, Filter } from 'lucide-react';
import { cn } from '../utils/cn';

export interface DataGridColumn<T> extends Omit<DataTableColumn<T>, 'filter' | 'filtering'> {
  accessor: keyof T & string;
  filterable?: boolean;
}

export interface DataGridProps<T extends object> {
  data: T[];
  columns: DataGridColumn<T>[];
  searchable?: boolean;
  selectable?: boolean;
  selectedIds?: (string | number)[];
  onSelectedChange?: (ids: (string | number)[]) => void;
  /**
   * Field on the row that uniquely identifies it. When omitted we
   * auto-derive: prefer `id`, else any key ending in `Id` (case-
   * insensitive). Pass explicitly when the heuristic guesses wrong.
   */
  rowId?: keyof T & string;
  defaultPageSize?: number;
  pageSizes?: number[];
  striped?: boolean;
  highlightOnHover?: boolean;
  withBorder?: boolean;
  title?: ReactNode;
  toolbar?: ReactNode;
  className?: string;
  minHeight?: number;
  onRowClick?: (row: T) => void;
  /** Show a "Columns" dropdown in the toolbar to toggle column visibility */
  columnPicker?: boolean;
  /**
   * Initial sort applied on first render. Defaults to the first column
   * ascending — that's fine for reference tables, but for chronological
   * grids (orders, invoices, payments) the caller should pass e.g.
   * `{ columnAccessor: 'orderDate', direction: 'desc' }` so newest is at
   * the top. The user can still re-sort by clicking any header.
   */
  initialSortStatus?: { columnAccessor: keyof T & string; direction: 'asc' | 'desc' };
}

export function DataGrid<T extends object>({
  data,
  columns,
  searchable = true,
  selectable = false,
  selectedIds,
  onSelectedChange,
  rowId,
  defaultPageSize = 10,
  pageSizes = [5, 10, 25, 50, 100],
  striped = true,
  highlightOnHover = true,
  withBorder = true,
  title,
  toolbar,
  className,
  minHeight = 300,
  onRowClick,
  columnPicker = false,
  initialSortStatus,
}: DataGridProps<T>) {
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<T>>(
    initialSortStatus ?? {
      columnAccessor: columns[0]?.accessor ?? '',
      direction: 'asc',
    },
  );
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(1);
  const [gotoPage, setGotoPage] = useState('');
  const [internalSelected, setInternalSelected] = useState<(string | number)[]>([]);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [openFilterCol, setOpenFilterCol] = useState<string | null>(null);
  const [colsMenuOpen, setColsMenuOpen] = useState(false);
  const [colsMenuPos, setColsMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const colsBtnRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!colsMenuOpen || !colsBtnRef.current) return;
    const r = colsBtnRef.current.getBoundingClientRect();
    setColsMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
  }, [colsMenuOpen]);

  useEffect(() => {
    if (!colsMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!colsBtnRef.current?.contains(e.target as Node)) setColsMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [colsMenuOpen]);

  // Auto-derive the row identifier when callers don't pass one. Without
  // this every consumer that didn't explicitly pass `rowId` was hitting
  // an undefined key — `r['id']` returned `undefined` for every row, so
  // `selected.includes(undefined)` matched everything once one box was
  // ticked, and the selected-count badge over-reported.
  const effectiveRowId = useMemo<keyof T & string>(() => {
    if (rowId) return rowId;
    const first = data[0] as Record<string, unknown> | undefined;
    if (first) {
      if ('id' in first) return 'id' as keyof T & string;
      const idKey = Object.keys(first).find((k) => /Id$/i.test(k));
      if (idKey) return idKey as keyof T & string;
    }
    return 'id' as keyof T & string;
  }, [rowId, data]);

  const selected = selectedIds ?? internalSelected;
  const setSelected = (v: (string | number)[]) => {
    if (onSelectedChange) onSelectedChange(v);
    else setInternalSelected(v);
  };

  const filterable = columns.filter((c) => c.filterable);

  const filtered = useMemo(() => {
    let rows = data;

    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        columns.some((c) => String(r[c.accessor] ?? '').toLowerCase().includes(q))
      );
    }

    for (const [key, value] of Object.entries(columnFilters)) {
      if (!value) continue;
      const v = value.toLowerCase();
      rows = rows.filter((r) => String(r[key as keyof T] ?? '').toLowerCase().includes(v));
    }

    const { columnAccessor, direction } = sortStatus;
    if (columnAccessor) {
      rows = [...rows].sort((a, b) => {
        const av = a[columnAccessor as keyof T];
        const bv = b[columnAccessor as keyof T];
        const cmp = String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
        return direction === 'asc' ? cmp : -cmp;
      });
    }

    return rows;
  }, [data, columns, search, columnFilters, sortStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const pageIds = paged.map((r) => r[effectiveRowId] as string | number);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const someSelected = !allPageSelected && pageIds.some((id) => selected.includes(id));

  const togglePage = () => {
    if (allPageSelected) {
      setSelected(selected.filter((id) => !pageIds.includes(id)));
    } else {
      const merged = Array.from(new Set([...selected, ...pageIds]));
      setSelected(merged);
    }
  };

  const toggleRow = (id: string | number) => {
    if (selected.includes(id)) setSelected(selected.filter((x) => x !== id));
    else setSelected([...selected, id]);
  };

  const renderCheckboxHeader = () => (
    <input
      type="checkbox"
      aria-label="Select all rows on page"
      checked={allPageSelected}
      ref={(el) => {
        if (el) el.indeterminate = someSelected;
      }}
      onChange={togglePage}
      className="h-4 w-4 cursor-pointer accent-[var(--color-primary)]"
    />
  );

  const visibleColumns = columns.filter((c) => !hiddenCols.has(c.accessor));

  // mantine-datatable's column generic resolves the discriminator-prop union
  // (ellipsis vs. noWrap) too tightly here — the compiler can't narrow our
  // mapped object back to the precise variant. The cast is safe because the
  // shapes are identical at runtime.
  const mantineColumns = [
    ...(selectable
      ? [
          {
            accessor: '__select__',
            title: renderCheckboxHeader() as unknown as string,
            width: 40,
            render: (r: T) => {
              const id = r[effectiveRowId] as string | number;
              return (
                <input
                  type="checkbox"
                  aria-label="Select row"
                  checked={selected.includes(id)}
                  onChange={() => toggleRow(id)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 cursor-pointer accent-[var(--color-primary)]"
                />
              );
            },
            sortable: false,
          } as DataTableColumn<T>,
        ]
      : []),
    ...visibleColumns.map((c) => ({
      ...c,
      sortable: c.sortable ?? true,
      // Filter opens only when the filter icon is clicked in the header.
      title: c.filterable ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <span className="flex-1">{(c.title as ReactNode) ?? c.accessor}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpenFilterCol((cur) => (cur === c.accessor ? null : c.accessor));
              }}
              aria-label="Toggle filter"
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-card-hover)] hover:text-[var(--color-text-primary)]',
                (columnFilters[c.accessor] || openFilterCol === c.accessor) &&
                  'text-[var(--color-primary)]'
              )}
            >
              <Filter size={11} />
            </button>
          </div>
          {openFilterCol === c.accessor && (
            <input
              autoFocus
              value={columnFilters[c.accessor] ?? ''}
              onChange={(e) => {
                setColumnFilters((s) => ({ ...s, [c.accessor]: e.target.value }));
                setPage(1);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpenFilterCol(null);
              }}
              placeholder="filter"
              className="h-6 w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] px-1.5 text-[11px] font-normal text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-primary)]"
            />
          )}
        </div>
      ) : (
        (c.title as ReactNode) ?? c.accessor
      ),
    })),
  ];

  const handleGoto = () => {
    const n = parseInt(gotoPage, 10);
    if (!isNaN(n) && n >= 1 && n <= totalPages) setPage(n);
    setGotoPage('');
  };

  return (
    <div className={cn('rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]', className)}>
      {(title || searchable || toolbar) && (
        <div className="flex flex-nowrap items-center gap-3 overflow-x-auto border-b border-[var(--color-border)] px-4 py-3">
          <div className="flex shrink-0 items-center gap-3">
            {title && <div className="whitespace-nowrap text-sm font-semibold text-[var(--color-text-primary)]">{title}</div>}
            {selectable && selected.length > 0 && (
              <span className="shrink-0 whitespace-nowrap rounded-full bg-[var(--color-primary-light)] px-2 py-0.5 text-xs font-medium text-[var(--color-primary)]">
                {selected.length} selected
              </span>
            )}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {searchable && (
              <>
                {searchOpen ? (
                  <div className="flex items-center gap-1 rounded-md border border-[var(--color-primary)] bg-[var(--color-bg-card)] px-2 py-1">
                    <Search size={14} className="text-[var(--color-text-muted)]" />
                    <input
                      autoFocus
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setSearchOpen(false);
                          setSearch('');
                        }
                      }}
                      placeholder="Search..."
                      className="w-40 bg-transparent text-xs text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-muted)] sm:w-56"
                    />
                    <button
                      type="button"
                      aria-label="Close search"
                      onClick={() => {
                        setSearchOpen(false);
                        setSearch('');
                      }}
                      className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-card-hover)] hover:text-[var(--color-text-primary)]"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    aria-label="Open search"
                    onClick={() => setSearchOpen(true)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--color-border)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-card-hover)] hover:text-[var(--color-text-primary)]"
                  >
                    <Search size={14} />
                  </button>
                )}
              </>
            )}
            {/* Columns picker — inlined into the toolbar row, sitting
                between the Search icon and the consumer-supplied toolbar
                (which holds Refresh + Add). Previously this lived in its
                own row below; consumers found that wasted a band of
                vertical space. */}
            {columnPicker && (
              <>
                <button
                  ref={colsBtnRef}
                  type="button"
                  onClick={() => setColsMenuOpen((o) => !o)}
                  aria-label="Toggle columns"
                  className="flex h-8 items-center gap-1 rounded-md border border-[var(--color-border)] px-2 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]"
                >
                  <Columns3 size={14} /> Columns
                </button>
                {colsMenuOpen &&
                  createPortal(
                    <div
                      style={{ position: 'fixed', top: colsMenuPos.top, right: colsMenuPos.right, zIndex: 9999 }}
                      className="w-56 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] py-1 shadow-xl"
                    >
                      {columns.map((c) => {
                        const visible = !hiddenCols.has(c.accessor);
                        return (
                          <label
                            key={c.accessor}
                            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]"
                          >
                            <input
                              type="checkbox"
                              checked={visible}
                              onChange={() => {
                                setHiddenCols((prev) => {
                                  const n = new Set(prev);
                                  if (visible) n.add(c.accessor);
                                  else n.delete(c.accessor);
                                  return n;
                                });
                              }}
                              className="h-3.5 w-3.5 accent-[var(--color-primary)]"
                            />
                            <span className="flex-1 truncate">
                              {(typeof c.title === 'string' ? c.title : c.accessor) || c.accessor}
                            </span>
                          </label>
                        );
                      })}
                    </div>,
                    document.body,
                  )}
              </>
            )}
            {toolbar}
          </div>
        </div>
      )}

      <div style={{ minHeight }}>
        <DataTable<T>
          idAccessor={effectiveRowId}
          onRowClick={onRowClick ? ({ record }) => onRowClick(record) : undefined}
          records={paged}
          columns={mantineColumns as DataTableColumn<T>[]}
          sortStatus={sortStatus}
          onSortStatusChange={setSortStatus}
          striped={striped}
          highlightOnHover={highlightOnHover}
          withTableBorder={withBorder}
          minHeight={minHeight}
          noRecordsText="No records"
          rowStyle={(row) => {
            const id = row[effectiveRowId] as string | number;
            const isSelected = selectable && selected.includes(id);
            return {
              fontSize: 13,
              backgroundColor: isSelected ? 'var(--color-primary-light)' : undefined,
              color: 'var(--color-text-primary)',
            };
          }}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] px-4 py-3 text-xs text-[var(--color-text-secondary)]">
        <div className="flex items-center gap-2">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="h-7 rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] px-1.5 text-xs text-[var(--color-text-primary)] outline-none"
          >
            {pageSizes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span>
            {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage(1)}
            disabled={currentPage === 1}
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-[var(--color-border)] disabled:opacity-40"
          >
            <ChevronsLeft size={14} />
          </button>
          <button
            type="button"
            onClick={() => setPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="h-7 rounded border border-[var(--color-border)] px-2 disabled:opacity-40"
          >
            Prev
          </button>
          <span className="min-w-[70px] text-center">
            Page {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="h-7 rounded border border-[var(--color-border)] px-2 disabled:opacity-40"
          >
            Next
          </button>
          <button
            type="button"
            onClick={() => setPage(totalPages)}
            disabled={currentPage === totalPages}
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-[var(--color-border)] disabled:opacity-40"
          >
            <ChevronsRight size={14} />
          </button>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleGoto();
            }}
            className="flex items-center gap-1"
          >
            <span>Go to:</span>
            <input
              value={gotoPage}
              onChange={(e) => setGotoPage(e.target.value)}
              className="h-7 w-14 rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] px-1.5 text-xs text-[var(--color-text-primary)] outline-none"
              placeholder="#"
            />
          </form>
        </div>
      </div>
    </div>
  );
}

export default DataGrid;
